import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

// A single global Real|Books toggle, replacing the per-page toggle button
// Purchase/Inventory/Sales each used to carry — one switch in the sidebar
// (next to the theme toggle) now drives all three lists at once, since the
// user never wants to flip it three separate times. Personal, per-browser
// preference (same reasoning as ThemeContext/WallpaperContext/Customize
// Columns) — never synced to BusinessSettings.
export type GlobalCategoryFilter = 'real' | 'books';

interface CategoryFilterContextValue {
  categoryFilter: GlobalCategoryFilter;
  toggleCategoryFilter: () => void;
}

const CategoryFilterContext = createContext<CategoryFilterContextValue | null>(null);

const STORAGE_KEY = 'global-category-filter';

function getInitial(): GlobalCategoryFilter {
  return localStorage.getItem(STORAGE_KEY) === 'books' ? 'books' : 'real';
}

export function CategoryFilterProvider({ children }: { children: ReactNode }) {
  const [categoryFilter, setCategoryFilter] = useState<GlobalCategoryFilter>(getInitial);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, categoryFilter);
  }, [categoryFilter]);

  function toggleCategoryFilter() {
    setCategoryFilter(f => (f === 'real' ? 'books' : 'real'));
  }

  return (
    <CategoryFilterContext.Provider value={{ categoryFilter, toggleCategoryFilter }}>
      {children}
    </CategoryFilterContext.Provider>
  );
}

export function useCategoryFilter(): CategoryFilterContextValue {
  const ctx = useContext(CategoryFilterContext);
  if (!ctx) throw new Error('useCategoryFilter must be used within a CategoryFilterProvider');
  return ctx;
}
